/**
 * Item Search / Reference Graph Query Node Configuration - similarity search or
 * reference-graph traversal, feeding discovered related items to whatever step
 * depends on this node (via per_related_item cardinality).
 */

import React, { useState } from 'react';
import type { PipelineNode } from '../types/pipeline';
import { SearchKind } from '../types/pipeline';
import './ItemSearchConfig.css';

const REFERENCE_TYPE_OPTIONS = ['REFERENCE', 'ASSOCIATION', 'OUTLINE_PARENT', 'INLINE_REFERENCE'] as const;
type GraphDirection = 'upstream' | 'downstream' | 'both';

export interface ItemSearchNodeConfig {
  search_kind: SearchKind;
  max_results: number;
  score_threshold?: number;
  graph_direction?: GraphDirection;
  max_depth?: number;
  reference_types?: string[];
}

interface ItemSearchConfigProps {
  node: PipelineNode;
  onSave: (config: ItemSearchNodeConfig) => void;
  onClose: () => void;
}

// task_type distinguishes the two palette entries (NodePalette.tsx) that both map to
// the same backend ItemSearchStep - search_kind is derived from it, not user-selectable.
export const ItemSearchConfig: React.FC<ItemSearchConfigProps> = ({ node, onSave, onClose }) => {
  const existing = node.config as Partial<ItemSearchNodeConfig> | undefined;
  const isReferenceGraph = node.task_type === 'reference_graph_query';

  const [maxResults, setMaxResults] = useState<number>(existing?.max_results ?? 5);
  const [scoreThreshold, setScoreThreshold] = useState<string>(
    existing?.score_threshold !== undefined ? String(existing.score_threshold) : ''
  );
  const [graphDirection, setGraphDirection] = useState<'' | GraphDirection>(existing?.graph_direction ?? '');
  const [maxDepth, setMaxDepth] = useState<string>(
    existing?.max_depth !== undefined ? String(existing.max_depth) : ''
  );
  const [referenceTypes, setReferenceTypes] = useState<string[]>(existing?.reference_types ?? []);

  const canSave = Number.isFinite(maxResults) && maxResults >= 1 && maxResults <= 50;

  const handleSave = () => {
    if (!canSave) return;
    const config: ItemSearchNodeConfig = {
      search_kind: isReferenceGraph ? SearchKind.REFERENCE_GRAPH : SearchKind.SIMILARITY,
      max_results: maxResults,
    };
    if (isReferenceGraph) {
      if (graphDirection) config.graph_direction = graphDirection;
      if (maxDepth.trim() !== '') config.max_depth = Number(maxDepth);
      if (referenceTypes.length > 0) config.reference_types = referenceTypes;
    } else if (scoreThreshold.trim() !== '') {
      config.score_threshold = Number(scoreThreshold);
    }
    onSave(config);
    onClose();
  };

  return (
    <div className="item-search-config">
      <div className="config-header">
        <div>
          <h3>{node.label || (isReferenceGraph ? 'Reference Graph Query' : 'Item Search')}</h3>
          <p className="section-description">
            {isReferenceGraph
              ? 'Traverse the reference graph to feed related items downstream'
              : 'Find similar items to feed downstream'}
          </p>
        </div>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="config-body">
        <div className="config-section">
          <div className="condition-row">
            <label>Max Results</label>
            <input
              type="number"
              className="condition-input"
              min={1}
              max={50}
              value={maxResults}
              onChange={(e) => setMaxResults(Number(e.target.value))}
            />
          </div>

          {isReferenceGraph ? (
            <>
              <div className="condition-row">
                <label>Direction (optional)</label>
                <select
                  className="condition-input"
                  value={graphDirection}
                  onChange={(e) => setGraphDirection(e.target.value as '' | GraphDirection)}
                >
                  <option value="">Use workflow default</option>
                  <option value="upstream">Upstream</option>
                  <option value="downstream">Downstream</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div className="condition-row">
                <label>Max Depth (optional)</label>
                <input
                  type="number"
                  className="condition-input"
                  min={1}
                  placeholder="Use workflow default"
                  value={maxDepth}
                  onChange={(e) => setMaxDepth(e.target.value)}
                />
              </div>

              <div className="condition-row">
                <label>Reference Types (optional)</label>
                <select
                  className="condition-input"
                  multiple
                  value={referenceTypes}
                  onChange={(e) => setReferenceTypes(Array.from(e.target.selectedOptions).map((o) => o.value))}
                >
                  {REFERENCE_TYPE_OPTIONS.map((rt) => (
                    <option key={rt} value={rt}>{rt}</option>
                  ))}
                </select>
                <p className="config-description">Ctrl/Cmd-click to select multiple. None selected = workflow default.</p>
              </div>
            </>
          ) : (
            <div className="condition-row">
              <label>Score Threshold (optional)</label>
              <input
                type="number"
                className="condition-input"
                step="0.01"
                min={0}
                max={1}
                placeholder="No minimum"
                value={scoreThreshold}
                onChange={(e) => setScoreThreshold(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="config-help">
          <h4>How this feeds downstream</h4>
          <ul>
            <li>
              Connect this node's output to any step - a plain dependency edge is enough. Give that step{' '}
              <strong>per_related_item</strong> cardinality and it will run once per discovered related item.
            </li>
            <li>
              Related item data is also available to custom agent nodes as{' '}
              <code>{'{related_item_summary}'}</code>, <code>{'{related_item_description}'}</code>, and{' '}
              <code>{'{related_item_id}'}</code> prompt variables.
            </li>
            <li>
              This node's discovered item ids are also available to any downstream agent's prompt via the{' '}
              <strong>Item field</strong> binding, as <code>results.&lt;this node&gt;.item_ids</code>.
            </li>
          </ul>
        </div>
      </div>

      <div className="config-footer">
        <button className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSave} disabled={!canSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
};
